import React, { useState, useEffect } from 'react';
import api from '../../api/axiosConfig';
import { Trash2 } from 'lucide-react';

interface Customer {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  source: 'Đăng ký nhận tin' | 'Form liên hệ';
  status: 'Đã tiếp nhận' | 'Đã hỗ trợ' | 'Đã huỷ';
  createdAt: string;
}

// Enum backend (hiển thị cũng là tiếng Việt)
const STATUS_OPTIONS = [
  { value: 'Đã tiếp nhận', label: 'Đã tiếp nhận' },
  { value: 'Đã hỗ trợ', label: 'Đã hỗ trợ' },
  { value: 'Đã huỷ', label: 'Đã huỷ' },
];

const QLKhachHang = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // --- Hàm tải danh sách khách hàng ---
  const fetchCustomers = async () => {
    try {
      const token = JSON.parse(sessionStorage.getItem('user') || '{}')?.accessToken;
      const { data } = await api.get('/contacts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers(data);
    } catch (error) {
      console.error('❌ Lỗi khi tải danh sách khách hàng:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Hàm cập nhật trạng thái ---
  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      const token = JSON.parse(sessionStorage.getItem('user') || '{}')?.accessToken;
      await api.put(
        `/contacts/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('✅ Cập nhật trạng thái thành công!');
      fetchCustomers();
    } catch (error: any) {
      console.error('❌ Lỗi khi cập nhật trạng thái:', error);
      window.alert(error.response?.data?.message || '❌ Cập nhật thất bại.');
    }
  };

  // --- Hàm xóa khách hàng ---
  const handleDeleteCustomer = async (id: string) => {
    if (!window.confirm('Bạn có chắc muốn xóa khách hàng này?')) return;
    try {
      const token = JSON.parse(sessionStorage.getItem('user') || '{}')?.accessToken;
      await api.delete(`/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.alert('✅ Xóa khách hàng thành công!');
      fetchCustomers();
    } catch (error: any) {
      console.error('❌ Lỗi khi xóa khách hàng:', error);
      window.alert(error.response?.data?.message || '❌ Xóa thất bại.');
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Quản Lý Khách Hàng / Liên hệ</h1>

      {/* Nút trở về Dashboard */}
      <div className="mb-6">
        <a
          href="/admin/dashboard"
          className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
        >
          ← Trở về Dashboard
        </a>
      </div>

      {isLoading ? (
        <p className="text-gray-600 text-center">Đang tải dữ liệu...</p>
      ) : customers.length === 0 ? (
        <p className="text-gray-600 text-center">Chưa có khách hàng nào.</p>
      ) : (
        <div className="overflow-x-auto shadow border rounded-lg">
          <table className="min-w-full border-collapse bg-white text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="border p-3 text-left">Tên khách hàng</th>
                <th className="border p-3 text-left">Email</th>
                <th className="border p-3 text-left">Số điện thoại</th>
                <th className="border p-3 text-left">Nguồn</th>
                <th className="border p-3 text-left">Tin nhắn</th>
                <th className="border p-3 text-center">Ngày gửi</th>
                <th className="border p-3 text-center">Trạng thái</th>
                <th className="border p-3 text-center">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c._id} className="hover:bg-gray-50">
                  <td className="border p-3">{c.name || '-'}</td>
                  <td className="border p-3">{c.email || '-'}</td>
                  <td className="border p-3">{c.phone || '-'}</td>
                  <td className="border p-3">{c.source}</td>
                  <td className="border p-3 max-w-[250px] truncate">{c.message || '-'}</td>
                  <td className="border p-3 text-center">
                    {new Date(c.createdAt).toLocaleDateString('vi-VN')}
                  </td>

                  {/* Trạng thái */}
                  <td className="border p-3 text-center">
                    <select
                      value={c.status}
                      onChange={(e) => handleUpdateStatus(c._id, e.target.value)}
                      className="border rounded p-1 text-sm"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Hành động */}
                  <td className="border p-3 text-center">
                    <button
                      onClick={() => handleDeleteCustomer(c._id)}
                      className="text-red-600 hover:text-red-800"
                      title="Xóa liên hệ"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default QLKhachHang;
